import { useEffect } from "react";

const SITE = "https://wishire.in";

/* Per-route head tags for the SPA. The prerender script writes the same tags
   into static HTML at build time; this keeps them right after client-side
   navigation (and in dev). */
function upsert(selector, create) {
  let el = document.head.querySelector(selector);
  if (!el) { el = create(); document.head.appendChild(el); }
  return el;
}
function meta(attr, key, content) {
  const el = upsert(`meta[${attr}="${key}"]`, () => { const m = document.createElement("meta"); m.setAttribute(attr, key); return m; });
  el.setAttribute("content", content || "");
}

export default function Seo({ title, description, path, type = "website", jsonLd, noindex = false }) {
  useEffect(() => {
    const fullTitle = title ? (title.includes("Wishire") ? title : `${title} | Wishire`) : "Wishire — AI Campus Placement Platform for Indian Colleges";
    const url = SITE + (path || window.location.pathname);
    document.title = fullTitle;
    meta("name", "description", description);
    meta("property", "og:title", fullTitle);
    meta("property", "og:description", description);
    meta("property", "og:url", url);
    meta("property", "og:type", type);
    meta("name", "twitter:title", fullTitle);
    meta("name", "twitter:description", description);
    meta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
    const canon = upsert('link[rel="canonical"]', () => { const l = document.createElement("link"); l.setAttribute("rel", "canonical"); return l; });
    canon.setAttribute("href", url);
    let ld = document.head.querySelector("script#seo-jsonld");
    if (jsonLd) {
      if (!ld) { ld = document.createElement("script"); ld.type = "application/ld+json"; ld.id = "seo-jsonld"; document.head.appendChild(ld); }
      ld.textContent = JSON.stringify(jsonLd);
    } else if (ld) {
      ld.remove();
    }
    return () => {};
  }, [title, description, path, type, jsonLd, noindex]);
  return null;
}

/* JSON-LD builders shared with scripts/prerender.js (kept in sync by hand; both tiny). */
export function pageJsonLd(page) {
  const url = SITE + page.path;
  const crumbs = [{ name: "Home", url: SITE + "/" }];
  const parts = page.path.split("/").filter(Boolean);
  let acc = "";
  parts.forEach((seg, i) => {
    acc += "/" + seg;
    crumbs.push({ name: i === parts.length - 1 ? page.h1 : seg.replace(/-/g, " "), url: SITE + acc });
  });
  const graph = [
    { "@type": page.kind === "guide" ? "Article" : "WebPage", "@id": url, url, name: page.title, headline: page.h1, description: page.description,
      isPartOf: { "@id": SITE + "/#site" }, publisher: { "@id": SITE + "/#org" }, inLanguage: "en-IN",
      ...(page.kind === "guide" ? { author: { "@type": "Organization", name: "Wishire" }, dateModified: "2026-10-09" } : {}) },
    { "@type": "BreadcrumbList", itemListElement: crumbs.map((c, i) => ({ "@type": "ListItem", position: i + 1, name: c.name, item: c.url })) },
  ];
  if (page.faq && page.faq.length) {
    graph.push({ "@type": "FAQPage", mainEntity: page.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) });
  }
  return { "@context": "https://schema.org", "@graph": graph };
}
